// Pure presentation logic for P4 history-derived suggestions — no React, no
// side effects. Extracted so the suggestion surfaces (RoomsSetup, onboarding
// ReviewStructure, Seasons) stay presentational and this logic is unit-testable.
//
// HARD RULE (CLAUDE.md): the frontend does ZERO math on suggestion values.
// Everything here is string conversion, calendar-month set logic, and
// verbatim formatting of backend-computed numbers. No rounding, no arithmetic
// on monetary values.

import { formatCount } from '@/lib/format'
import type { SeasonSuggestion } from '@/types/shared'

/**
 * The evidence behind a base-rate suggestion, in as few characters as it can
 * honestly be said: `312 stays`, `1 stay`, `1,240 stays`.
 *
 * A suggestion without its sample size is just a number someone made up — the
 * count is what makes it trustworthy — but it rides inside a table cell that
 * must not wrap, so it is a count and a noun and nothing else. `formatCount`
 * only groups digits; the backend's number is rendered verbatim.
 */
export function sampleEvidenceLabel(sampleSize: number): string {
  return `${formatCount(sampleSize)} ${sampleSize === 1 ? 'stay' : 'stays'}`
}

/**
 * Stage a suggested base rate into a room's editor snapshot: the suggestion
 * lands in the first base-rate field (occupancy 1) ONLY if that field is
 * empty — the backend sent one number, so we prefill one field and invent
 * nothing else. Returns a new object; the caller passes it to the room editor
 * as its initial draft, so nothing is staged page-side until the operator
 * presses Apply (and nothing saves until Review & Save).
 */
export function prefillSuggestedRates(
  current: Record<string, string>,
  suggestedAmount: number
): Record<string, string> {
  const existing = current['1']
  if (existing && existing.trim() !== '') return { ...current }
  return { ...current, '1': String(suggestedAmount) }
}

/**
 * The calendar months (1..12, deduped) spanned by an ISO date range,
 * inclusive of both endpoints. Ranges longer than a year cover all 12.
 * String inspection only — no Date arithmetic on rates.
 */
export function monthsSpanned(startDate: string, endDate: string): number[] {
  const [startYear, startMonth] = startDate.split('-').map(Number)
  const [endYear, endMonth] = endDate.split('-').map(Number)
  if (!startYear || !startMonth || !endYear || !endMonth) return []

  const months: number[] = []
  let year = startYear
  let month = startMonth
  // Guard: 24 iterations covers any range; >12 months already spans all 12.
  for (let i = 0; i < 24; i++) {
    if (!months.includes(month)) months.push(month)
    if (year === endYear && month === endMonth) break
    if (year > endYear || (year === endYear && month > endMonth)) break
    month += 1
    if (month > 12) {
      month = 1
      year += 1
    }
  }
  return months
}

/**
 * Presentation-level filter: a suggestion whose months overlap ANY month
 * already covered by an existing season does not render — once the operator
 * has a season there (added from the suggestion or by hand), we stop talking
 * about it. Derived purely from the existing-seasons prop.
 */
export function visibleSeasonSuggestions(
  suggestions: SeasonSuggestion[],
  existingSeasons: { startDate: string; endDate: string }[]
): SeasonSuggestion[] {
  const covered = new Set<number>()
  for (const season of existingSeasons) {
    for (const month of monthsSpanned(season.startDate, season.endDate)) {
      covered.add(month)
    }
  }
  return suggestions.filter((s) => !s.months.some((m) => covered.has(m)))
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

/**
 * Month range in words: "June – August", "December – January" (wrapping),
 * or a single "July".
 */
export function monthRangeLabel(startMonth: number, endMonth: number): string {
  const start = MONTH_NAMES[startMonth - 1] ?? ''
  const end = MONTH_NAMES[endMonth - 1] ?? ''
  if (startMonth === endMonth) return start
  return `${start} – ${end}`
}

/**
 * Signed percentage, rendered exactly as the backend sent it (backend already
 * rounded to 1 decimal): 30 → "+30%", 7.5 → "+7.5%", -12 → "-12%".
 * No toFixed, no rounding.
 */
export function formatSignedPct(pct: number): string {
  return `${pct > 0 ? '+' : ''}${pct}%`
}
